import { DepressionContentPage } from "@/components/DepressionContentPage";
import type { RelatedLink } from "@/components/RelatedPages";

const relatedLinks: RelatedLink[] = [
  {
    title: "I Never Had Depression Before. Why Now?",
    href: "/sudden-depression",
    description: "Depression can appear even without a previous history.",
  },
  {
    title: "When Anxiety, Insomnia and Depression Overlap",
    href: "/depression-anxiety-insomnia",
    description: "How overlapping symptoms can affect mood and daily life.",
  },
  {
    title: "What Happens During a TMS Consultation?",
    href: "/what-to-expect-from-your-consultation",
    description: "What to expect during an evaluation.",
  },
];

export default () => (
  <DepressionContentPage
    seo={{
      title: "Helping Someone You Love With Depression",
      description:
        "Learn the changes families often notice when someone is struggling with depression, anxiety or loss of functioning — and how to help them seek care.",
      canonical: "/helping-someone-with-depression",
      keywords:
        "helping someone with depression, family depression support, signs of depression, how to help depressed loved one, mental health",
    }}
    badge="Family Resource"
    heroTitle={"\u201CThey're Just Not Themselves Anymore.\u201D"}
    heroSubtitle="Family members and friends often notice changes in everyday behavior before they know exactly what is wrong."
    heroCtaPrimary="Check My Insurance Options"
    heroCtaPrimaryHref="/check-my-insurance-options"
    sections={[
      {
        heading: "Changes Families Often Notice",
        variant: "cards",
        intro:
          "Depression does not always look like visible sadness. Family members often notice changes in everyday behavior first. Some common observations include:",
        cards: [
          {
            title: "Sleep Changes",
            body: "Sleeping much more or much less than usual.",
          },
          {
            title: "Loss of Interest",
            body: "No longer engaging in activities they used to enjoy.",
          },
          {
            title: "Social Withdrawal",
            body: "Pulling back from friends, family and social situations.",
          },
          {
            title: "Appetite Changes",
            body: "Eating much more or much less than is typical for them.",
          },
          {
            title: "Less Motivation",
            body: "Struggling to start or follow through on everyday tasks.",
          },
          {
            title: "Difficulty Concentrating",
            body: "Having trouble focusing or remembering things.",
          },
          {
            title: "Constant Anxiety",
            body: "Appearing on edge, restless or worried much of the time.",
          },
          {
            title: "Neglected Routines",
            body: "Letting go of normal self-care or daily routines.",
          },
          {
            title: "Exhaustion",
            body: "Saying they feel exhausted, even without obvious exertion.",
          },
          {
            title: "Hopelessness",
            body: "Expressing feeling overwhelmed, stuck or hopeless.",
          },
        ],
      },
      {
        heading: 'Do Not Focus Only on Whether They "Look Sad"',
        variant: "text",
        body: (
          <p>
            Depression can show up through functioning, motivation, sleep,
            anxiety and withdrawal — not only through visible sadness. Someone
            can be experiencing significant depression without appearing tearful
            or overtly sad. Paying attention to changes in how they function day
            to day can be more informative than looking for sadness alone.
          </p>
        ),
      },
      {
        heading: "How to Start the Conversation",
        variant: "callout",
        callout:
          "\"I've noticed you haven't seemed like yourself lately, and I'm concerned about you. Would you be open to talking with someone?\"",
      },
      {
        heading: "How to Start the Conversation (Continued)",
        variant: "text",
        intro:
          "The goal is to express care without pressure or judgment. A few principles that tend to help:",
        body: (
          <ul className="list-disc space-y-2 pl-6">
            <li>Choose a calm moment — not during a crisis or an argument.</li>
            <li>
              Share specific observations rather than labels or diagnoses.
            </li>
            <li>Avoid language that sounds like blame or disappointment.</li>
            <li>Offer to help with the next step without taking over.</li>
            <li>Accept that a no today is not permanent.</li>
          </ul>
        ),
      },
      {
        heading: "When Professional Help May Be Appropriate",
        variant: "text",
        intro:
          "It may be time to encourage professional help when symptoms persist, worsen, or interfere with daily functioning, work or relationships.",
        body: (
          <p>
            If you are worried about someone's immediate safety — if they have
            expressed thoughts of self-harm or suicide — this is not the moment
            for a scheduled consultation. Call or text 988 (Suicide & Crisis
            Lifeline, available 24/7), call 911, or go to your nearest emergency
            room.
          </p>
        ),
      },
      {
        heading: "You Can Help Them Take the First Step",
        variant: "text",
        intro:
          "Family members can play a meaningful role without making medical decisions for a competent adult. You can:",
        body: (
          <ul className="list-disc space-y-2 pl-6">
            <li>Help research treatment options</li>
            <li>Attend consultations when appropriate and invited</li>
            <li>Help gather insurance information</li>
            <li>Provide observations about changes you have noticed</li>
            <li>
              Offer logistical support, such as transportation or scheduling
            </li>
          </ul>
        ),
      },
      {
        heading: "What Happens During an Evaluation?",
        variant: "text",
        intro:
          "Understanding what an evaluation involves can make the conversation easier. An evaluation typically discusses symptoms, onset, sleep, mood, treatment history and relevant medical history.",
        body: (
          <p>
            <a
              href="/what-to-expect-from-your-consultation"
              className="text-primary font-semibold underline"
            >
              Learn what happens during a consultation.
            </a>
          </p>
        ),
      },
    ]}
    faq={[
      {
        question: "How can I tell if someone I love may be depressed?",
        answer:
          "Look for changes in everyday behavior — sleep, appetite, motivation, social activity, concentration and mood. Depression does not always look like visible sadness. Withdrawal, exhaustion, anxiety and loss of interest can all be signs.",
      },
      {
        question: "What if they say they are just tired?",
        answer:
          "Fatigue and exhaustion can be part of depression. If tiredness is persistent, accompanied by other changes — sleep disruption, withdrawal, loss of interest or low mood — it may be worth discussing with a professional.",
      },
      {
        question: "How do I bring up treatment without upsetting them?",
        answer:
          "Choose a calm moment, share specific observations rather than labels, and express care without pressure. A simple approach: \"I've noticed you haven't seemed like yourself lately, and I'm concerned about you. Would you be open to talking with someone?\"",
      },
      {
        question: "Can I schedule a consultation for a family member?",
        answer:
          "A family member may contact our center to learn general information about services and the consultation process. The decision to begin treatment belongs to the patient, and they must consent to and participate in treatment themselves.",
      },
      {
        question: "Can family attend an evaluation?",
        answer:
          "Family members may attend consultations when appropriate and when the patient invites them. Their observations about changes in behavior can be valuable. The patient's consent and comfort guide what involvement looks like.",
      },
      {
        question: "What if I am worried about their immediate safety?",
        answer:
          "If you are worried about immediate safety — if they have expressed thoughts of self-harm or suicide — call or text 988 (Suicide & Crisis Lifeline, available 24/7), call 911, or go to your nearest emergency room. This is not the moment for a scheduled outpatient consultation.",
      },
      {
        question: "How do I learn whether insurance may cover treatment?",
        answer:
          "Our team can help verify insurance benefits at no cost. You can submit insurance information through our verification form, and we will contact the insurer to determine what your plan may cover.",
      },
    ]}
    faqHeading="Frequently Asked Questions"
    relatedLinks={relatedLinks}
    finalCtaHeading="You Can Help Someone You Love Take the Next Step"
    finalCtaText="You don't have to navigate this alone. Our team can help you understand the options available so you can have an informed conversation with the person you care about."
    finalCtaPrimaryLabel="Check My Insurance Options"
    finalCtaPrimaryHref="/check-my-insurance-options"
    finalCtaSecondaryLabel="Schedule a Private Consultation"
    finalCtaSecondaryHref="/contactus"
  />
);
